<template>
  <v-container class="pa-0">
    <Loader v-if="loading" text="" color="black"></Loader>

    <div v-if="!loading">
      <div
        @click="doMapClick()"
        class="text-right mr-3"
        v-if="hotspotCount > 0"
      >
        <a>View on Map</a>
      </div>
      <v-row>
        <v-col class="hyp-scroll" id="hotspot-list-container">
          <div
            class="hotspots-holder"
            v-for="(country, index) in countriesFiltered"
            :key="'country' + index"
          >
            <v-card
              class="hotspot pa-3 mb-3"
              v-for="(hotspot, index) in country.hotspots"
              :key="'hotspot' + index"
            >
              <div @click="doMapClick(hotspot)">
                <v-card-title class="hotspot-title pa-0 mb-5">{{
                  getHotspotTitle(hotspot)
                }}</v-card-title>
                <v-card-subtitle class="hotspot-topic-display pa-0">
                  <strong>Event:</strong> {{ getHotspotTopic(hotspot) }}
                </v-card-subtitle>
                <em class="hotspot-date-display pa-0">
                  {{ getHotspotStartTime(hotspot) }}
                </em>
              </div>
            </v-card>
          </div>

          <p
            v-if="!countriesFiltered || !countriesFiltered.length"
            class="text-center"
          >
            There are no hotspots in this location currently.
          </p>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashDebounce from "lodash/debounce";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";

import { getNowRounded15Min, hoursAgo24 } from "@/common/date.utilities";
import {
  getHotspots,
  addToMap as addHotspotsToMap,
} from "@/services/grids/hotspots.service";
import { fakeMapFeature, removeMapData } from "@/common/maps/features";
import { MAIN_MAP_ID, MAP_ICON_NAMES } from "@/common/constants/maps";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { LeftSidePanelWidthNumber } from "@/common/constants/layout";
import {
  MODULE_PREFIX_HOTSPOTS,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_HOME,
} from "@/store";
import {
  SET_HOTSPOTS_DETAILS,
  SET_RESET_MAP_ICONS,
} from "@/store/mutations.type";
import { GET_DETAILS_SIDEBAR } from "@/store/actions.type";
import { mapGetters } from "vuex";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  CLICK_CLOSE: "click-close",
  CLICK_MINIMIZE: "click-minimize",
  OPEN_HOTSPOT_DETAILS: "open-hotspot-details",
};

export default {
  name: "RisksCountryHotspots",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    selectedLocation: {
      type: Object,
      required: true,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    enableScroll: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dateStart: hoursAgo24(),
      dateEnd: getNowRounded15Min(),
      countries: [],
      page: 1,
      loading: false,
      lastTimestamp: new Date().valueOf(),
      hotspotCount: 0,
      hotspotCountDescription: "",
    };
  },
  created() {
    this.EMITTED_ACTIONS = EMITTED_ACTIONS;
  },
  mounted() {
    this.getContent();

    //Resize the list so it scrolls
    this.resizeList();
    window.addEventListener("resize", this.resizeList);
  },
  updated() {
    this.resizeList();
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    countriesFiltered() {
      return this.countries;
    },
  },
  methods: {
    getContent(page, $state, params = {}) {
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.loading = true;

      this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (
      page,
      $state,
      params = {}
    ) {
      try {
        let dataParams = this.getParams(params);
        dataParams.includeText = false;
        dataParams.includeCountryInText = false;

        //Handle a race condition where filters are rapidly changed and server responses
        //don't come back in order by tracking the last time a request was sent
        this.lastTimestamp = new Date().valueOf();
        const thisTimestamp = this.lastTimestamp;

        let result = await getHotspots(dataParams);

        //Exit if this is not the results of our last request
        if (thisTimestamp !== this.lastTimestamp) {
          return;
        }
        this.hotspotCount = lodashGet(result, "features.length") || 0;

        let countries = result.features.reduce((accum, feature) => {
          let country = feature.properties.location_metadata.countryCode;
          if (!accum[country]) {
            accum[country] = [];
          }
          accum[country].push(feature);
          return accum;
        }, {});
        countries = Object.entries(countries).map((country) => {
          return {
            iso2: country[0],
            hotspots: country[1],
          };
        });
        this.countries = countries;
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    500),
    getParams() {
      let params = lodashCloneDeep(this.params) || {};

      params.start = this.dateStart;
      params.end = this.dateEnd;

      delete params.pageOffset;
      delete params.pageSize;
      params.includeText = true;
      params.includeCountryInText = false;

      params.aoiId = params.countryCode;

      return params;
    },
    resetState() {
      this.countries = [];
      this.page = 1;
    },
    /**
     * Resizes the hotspot list element to fit inside the window.
     * Set enableScroll prop to false to let the list expand to full height
     */
    resizeList() {
      if (this.enableScroll) {
        let el = document.getElementById("hotspot-list-container");
        if (el) {
          el.style.height =
            window.innerHeight - el.getBoundingClientRect().y - 5 + "px";
        }
      }
    },
    async clearDataLayers() {
      await this.$store.commit(
        MODULE_PREFIX_MAP + SET_RESET_MAP_ICONS,
        new Date()
      );
    },
    async doMapClick(hotspot) {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE);

      if (hotspot) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_hotspots,
          EVENT_ACTIONS.clicked,
          "Country risk hotspot card view on map"
        );
      } else {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_hotspots,
          EVENT_ACTIONS.clicked,
          "Country risk hotspots view on map"
        );
      }
      await this.clearDataLayers();
      const LAYER_ID = "layer-rs-hotspots";
      let params = {
        id: LAYER_ID,
        layerId: LAYER_ID,
        map: this.map,
        mapClickEvent: lodashDebounce((e, features) => {
          e.originalEvent.stopPropagation();
          this.doMapClick(features[0]);
        }, 100),
        filters: ["positive"],
        start: this.dateStart,
        end: this.dateEnd,
        pageSize: 10000,
        noCluster: true,
        iconColor: "red",
        iconName: MAP_ICON_NAMES.hotspots,
        aoiId: this.params.countryCode.toLowerCase(),
      };
      addHotspotsToMap(params);

      if (hotspot) {
        let details = {
          feature: fakeMapFeature(hotspot),
        };

        this.$store.commit(
          MODULE_PREFIX_HOTSPOTS + SET_HOTSPOTS_DETAILS,
          details
        );

        this.detailsSidebar.features = [fakeMapFeature(hotspot)];
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.monitorType = "hotspots";
        this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );

        this.$emit(EMITTED_ACTIONS.OPEN_HOTSPOT_DETAILS, true);
        //Pan and zoom the map to the bounding box with some padding so that hotspots aren't on the edge of the map.

        this.map.flyTo({
          zoom: 11,
          center: hotspot.geometry.coordinates,
          padding: {
            top: 100,
            bottom: 100,
            left: 100,
            right:
              (this.leftSidebarWidth || 0) +
              (this.detailsSidebar.width || 0) +
              100,
          },
        });

        //Get information for the analytics tracker
        const location = fakeMapFeature(hotspot)?.properties?.location_metadata;
        let analyticsDetails = "";
        if (location) {
          analyticsDetails = JSON.parse(location)?.country;
          if (analyticsDetails) {
            analyticsDetails += ". Country: " + analyticsDetails;
          }
        }
      } else {
        if (
          this.map &&
          this.selectedLocation &&
          this.selectedLocation.minLongitude &&
          this.selectedLocation.minLatitude &&
          this.selectedLocation.maxLongitude &&
          this.selectedLocation.maxLatitude
        ) {
          const bbox = [
            [
              this.selectedLocation.minLongitude,
              this.selectedLocation.minLatitude,
            ],
            [
              this.selectedLocation.maxLongitude,
              this.selectedLocation.maxLatitude,
            ],
          ];
          const fitBoundsPadding = 40;
          this.map.fitBounds(bbox, {
            padding: {
              top: fitBoundsPadding,
              bottom: fitBoundsPadding,
              right: fitBoundsPadding + LeftSidePanelWidthNumber,
              left: fitBoundsPadding + LeftSidePanelWidthNumber,
            },
          });
        }
      }
    },
    getHotspotTitle(hotspot) {
      return lodashGet(hotspot, "properties.headline.title");
    },
    getHotspotStartTime(hotspot) {
      return lodashGet(hotspot, "properties.formatted.startTime");
    },
    getHotspotTopic(hotspot) {
      return lodashGet(hotspot, "properties.topics")
        .join(" | ")
        .replace("_", " ");
    },
  },
  watch: {
    params: {
      async handler() {
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    hotspotCount: {
      handler() {
        this.hotspotCountDescription =
          `There are ${this.hotspotCount} hotspot` +
          (this.hotspotCount === 1 ? "" : "s") +
          ` in these locations`;
      },
    },
  },
};
</script>

<style scoped>
#hotspot-list-container {
  overflow-y: auto;
}
.v-card__subtitle,
.hotspot-date-display {
  font-size: 12px;
}
.hotspot-title {
  font-size: 14px;
  line-height: 1.4em;
  word-break: break-word;
}
.hotspot {
  cursor: pointer;
}
</style>
